
export const secondsToFormattedString = (s: number) => {
  const minutes = Math.floor(s / 60);
  const seconds = s - (minutes * 60);
  return `${minutes > 0 ? `${minutes}` : `0`}:${seconds < 10 ? `0${seconds}` : seconds}`
}

export const SECOND = 1000;
export const MINUTE = SECOND * 60;
export const HOUR = MINUTE * 60;
export const DAY = HOUR * 24;

export const daysAfterTimestamp = (timestamp: number) => Math.floor((Date.now() - timestamp) / DAY)

export const daysAfterDate = (dateString: string) => daysAfterTimestamp(new Date(dateString).getTime())

export const convert24hourTo12hour = (hours: number, mins?: number) => {
  const period = hours < 12 ? 'AM' : 'PM';
  hours = hours % 12;
  hours = hours ? hours : 12; // the hour '0' should be '12'

  return `${hours}${mins !== undefined ? `:${mins.toString().padStart(2, '0')}` : ``}\u00A0${period}`;
}

export const addHours = (date: Date, hours: number) => {
  return new Date(date.getTime() + (hours * HOUR));
}

export const addMinutes = (date: Date, minutes: number) => {
  return new Date(date.getTime() + (minutes * MINUTE));
}

export const getDateString = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

export const formatDateString = (dateString?: string) => {
  if (!dateString) return '';
  return dateString.split('-').reverse().join('/');
}

export const getTimeString = (date: Date) => {
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${hours}:${minutes}`;
}

export const mergeDateAndTime = (date: Date, time: Date) => {
  // Get the date components
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();

  // Get the time components
  const hours = time.getHours();
  const minutes = time.getMinutes();
  const seconds = time.getSeconds();
  const milliseconds = time.getMilliseconds();

  // Create a new date with the date component from the first
  // and the time component from the second
  return new Date(year, month, day, hours, minutes, seconds, milliseconds);
}

export const stripTime = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

export const isDateInRange = (target: Date, start: Date, end: Date) => {
  if (!target || !start || !end) return false;

  const targetDate = stripTime(target);
  const startDate = stripTime(start);
  const endDate = stripTime(end);

  return targetDate.getTime() >= startDate.getTime() && targetDate.getTime() <= endDate.getTime();
}

export const isDateBefore = (target: Date, comparison: Date) => {
  if (!target || !comparison) return false;

  const targetDate = stripTime(target);
  const comparisonDate = stripTime(comparison);

  return targetDate.getTime() < comparisonDate.getTime();
}

export const isDateAfter = (target: Date, comparison: Date) => {
  if (!target || !comparison) return false;

  const targetDate = stripTime(target);
  const comparisonDate = stripTime(comparison);

  return targetDate.getTime() > comparisonDate.getTime();
}

export const isSameDay = (date1: Date, date2: Date) => {
  if (!date1 || !date2) return false;
  return stripTime(date1).getTime() === stripTime(date2).getTime();
}

export const setHours = (date: Date, hours: number) => new Date(date.getFullYear(), date.getMonth(), date.getDate(), hours);

export const roundUpToNextHour = (date: Date) => {
  const newDate = new Date(date.getTime());
  if (newDate.getMinutes() === 0) {
    // If the date is already at the beginning of an hour, return it as is
    newDate.setSeconds(0);
    newDate.setMilliseconds(0);

    return newDate;
  } else {
    // Set minutes, seconds, and milliseconds to zero
    newDate.setMinutes(0);
    newDate.setSeconds(0);
    newDate.setMilliseconds(0);

    // Add one hour
    newDate.setHours(newDate.getHours() + 1);
  }

  return newDate;
}

export const updateToWorkingHours = (date: Date, min: number, max: number) => {
  const newDate = new Date(date.getTime());
  if (newDate.getHours() < min) {
    newDate.setHours(min);
  } else if (newDate.getHours() > max) {
    newDate.setDate(newDate.getDate() + 1);
    newDate.setHours(min);
  }

  return newDate;
}

export const getTimezoneOffset = (timeZone: string, at: Date = new Date()) => {
  const utcTime = at.getTime();

  // Format BOTH date AND time in the target timezone to avoid UTC date/timezone date mismatch
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(at);

  const get = (type: string) => parts.find(p => p.type === type)?.value ?? '00';
  const localDateString = `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}:${get('second')}Z`;

  const localTimestamp = new Date(localDateString).getTime();
  return (localTimestamp - utcTime) / 60000;
}

export const localDateToUTCString = (date: Date, timeZone: string) => {
  // Anchor the offset lookup on the picked wall clock (interpreted as UTC), not on
  // the device-dependent instant — otherwise far-from-Bermuda devices can land on
  // the wrong side of a DST transition and pick up the wrong offset. Then refine
  // once at the resulting instant to settle wall clocks that sit between the
  // UTC-side and target-side of a DST boundary (e.g. 03:30 on spring-forward day).
  const wallAsUTC = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  const initialOffset = getTimezoneOffset(timeZone, wallAsUTC);
  const offset = getTimezoneOffset(timeZone, new Date(wallAsUTC.getTime() - initialOffset * 60000));

  // Calculate the UTC time by adjusting the local time by the offset
  let utcDate = new Date(date.getTime() - (date.getTimezoneOffset() * 60000) - (offset * 60000));

  // Format the UTC date
  let utcYear = utcDate.getUTCFullYear();
  let utcMonth = String(utcDate.getUTCMonth() + 1).padStart(2, '0');
  let utcDay = String(utcDate.getUTCDate()).padStart(2, '0');
  let utcHour = String(utcDate.getUTCHours()).padStart(2, '0');
  let utcMinute = String(utcDate.getUTCMinutes()).padStart(2, '0');
  let utcSecond = String(utcDate.getUTCSeconds()).padStart(2, '0');

  // Construct the UTC time string
  return `${utcYear}-${utcMonth}-${utcDay}T${utcHour}:${utcMinute}:${utcSecond}Z`;
}

export const utcStringToLocalDate = (string: string, timeZone: string) => {
  const date = new Date(string);
  const offset = getTimezoneOffset(timeZone, date);
  return new Date(date.getTime() + (date.getTimezoneOffset() * 60000) + (offset * 60000));
}

export const dateToString = (date: Date) => {
  let year = date.getFullYear();
  let month = String(date.getMonth() + 1).padStart(2, '0');
  let day = String(date.getDate()).padStart(2, '0');
  let hour = String(date.getHours()).padStart(2, '0');
  let minute = String(date.getMinutes()).padStart(2, '0');
  let second = String(date.getSeconds()).padStart(2, '0');

  return `${year}-${month}-${day}T${hour}:${minute}:${second}Z`;
}

export const stringToDate = (string: string) => {
  // Remove trailing Z so the string is parsed as local time, not UTC
  const localString = string.endsWith('Z') ? string.slice(0, -1) : string;
  return new Date(localString);
}